import React, { useCallback, useMemo } from "react";
import { DropdownItem } from "reactstrap";
import { useIntl } from "react-intl";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useRouterCore } from "../../../cores/router";
import { MessageType } from "../../../cores/socket/constant/messageType.constant";
import { NotificationType } from "../../../cores/notifications/constants/notificationType.constant";
import {
  ContactExportedNotification,
  ContactsImportedNotification,
  ConversationNotification,
  ImportingProcessNotification,
  MessageNotification,
  NewContactsNotification,
  Notification,
  SocialPostNotification,
  SubscriptionNotification,
  TaskAssignmentNotification,
} from "../../../cores/notifications/interfaces/api/get-notifications";
import { useWebChatCore } from "../../../cores/conversations";
import ConversationNotificationItem from "./ConversationNotificationItem";
import SocialPostNotificationItem from "./SocialPostNotificationItem";
import MessageNotificationItem from "./MessageNotificationItem";
import { useNotificationCore } from "../../../cores/notifications";
import { useCompanyCore } from "../../../cores/company";
import SubscriptionNotificationItem from "./SubscriptionNotificationItem";
import ContactsImportedNotificationItem from "./ContactsImportedNotificationItem";
import { useSocialChatCore } from "../../../cores/socialChat";
import ContactExportedNotificationItem from "./ContactExportedNotificationItem";
import TaskAssignmentNotificationItem from "./TaskAssignmentNotificationItem";
import NewContactNotificationItem from "./NewContactsNotificationItem";
import ImportingProcessNotificationItem from "./ImportingProcessNotificationItem";
import cn from "classnames";
import { useGlobalCore } from "../../../cores/globals";
import {
  formatNotificationAbsoluteTime,
  formatNotificationRelativeTime,
} from "../../utils/notificationDisplay";
import "./NotificationContainer.scss";

interface Props {
  notification: Notification;
  unread?: boolean;
  /** Dropdown navbar vs página /notifications */
  variant?: "dropdown" | "page";
}

function useNotificationKindLabel(notification: Notification): string {
  const intl = useIntl();
  return useMemo(() => {
    switch (notification.type) {
      case NotificationType.MESSAGE:
        return intl.formatMessage({ id: "app.notifications.kind.message" });
      case NotificationType.CONVERSATION:
        return intl.formatMessage({ id: "app.notifications.kind.conversation" });
      case NotificationType.SOCIAL_POST:
        return intl.formatMessage({ id: "app.notifications.kind.socialPost" });
      case NotificationType.SUBSCRIPTION:
        return intl.formatMessage({ id: "app.notifications.kind.subscription" });
      case NotificationType.CONTACTS_IMPORTED:
        return intl.formatMessage({ id: "app.notifications.kind.contactsImported" });
      case NotificationType.IMPORTING_PROCESS:
      case NotificationType.IMPORTING_PROCESS_FINISHED:
        return intl.formatMessage({ id: "app.notifications.kind.importingProcess" });
      case NotificationType.CONTACT_EXPORTED:
        return intl.formatMessage({ id: "app.notifications.kind.contactExported" });
      case NotificationType.NEW_CONTACT:
        return intl.formatMessage({ id: "app.notifications.kind.newContact" });
      case NotificationType.TASK_ASSIGNMENT:
        return intl.formatMessage({ id: "app.notifications.kind.taskAssignment" });
      default:
        return intl.formatMessage({ id: "app.notifications.kind.other" });
    }
  }, [notification.type, intl]);
}

export default function NotificationContainer(props: Props) {
  const { notification, unread, variant = "dropdown" } = props;
  const intl = useIntl();
  const { isDarkMode } = useGlobalCore();
  const kindLabel = useNotificationKindLabel(notification);

  const { company } = useCompanyCore();
  const { goToWebChat } = useRouterCore();
  const {
    putReadNotification,
    getUnreadNotifications,
    getNotifications,
    deleteNotification,
  } = useNotificationCore();
  const { selectWebConversation } = useWebChatCore();
  const {
    selectFacebookConversation,
    selectInstagramConversation,
    selectWhatsappConversation,
  } = useSocialChatCore();

  const handleSelectNotification = useCallback(async () => {
    if (!company?.id) return;
    await putReadNotification(company.id, notification.id);
    getUnreadNotifications(company.id);
    getNotifications(company.id, 1);

    if (notification.type !== NotificationType.MESSAGE) return;

    const ntf = notification.data as MessageNotification;
    switch (ntf.type) {
      case MessageType.FACEBOOK:
        selectFacebookConversation(ntf.id);
        goToWebChat();
        break;
      case MessageType.WHATSAPP:
        selectWhatsappConversation(ntf.id);
        goToWebChat();
        break;
      case MessageType.INSTAGRAM:
        selectInstagramConversation(ntf.id);
        goToWebChat();
        break;
      case MessageType.WEB:
        selectWebConversation(ntf.id);
        goToWebChat();
        break;
      default:
        break;
    }
  }, [
    notification,
    company,
    goToWebChat,
    putReadNotification,
    getUnreadNotifications,
    getNotifications,
    selectWebConversation,
    selectFacebookConversation,
    selectInstagramConversation,
    selectWhatsappConversation,
  ]);

  const getNotificationItem = useCallback(() => {
    const compact = variant === "dropdown";
    const page = variant === "page";
    switch (notification.type) {
      case NotificationType.MESSAGE:
        return (
          <MessageNotificationItem
            notification={notification.data as MessageNotification}
            variant={variant}
            isDarkMode={isDarkMode}
          />
        );
      case NotificationType.CONVERSATION:
        return (
          <ConversationNotificationItem
            notification={notification.data as ConversationNotification}
            variant={variant}
            isDarkMode={isDarkMode}
          />
        );
      case NotificationType.SOCIAL_POST:
        return (
          <SocialPostNotificationItem
            notification={notification.data as SocialPostNotification}
            compact={compact}
            isDarkMode={isDarkMode}
            suppressFooterTime={page}
          />
        );
      case NotificationType.SUBSCRIPTION:
        return (
          <SubscriptionNotificationItem
            notification={notification.data as SubscriptionNotification}
          />
        );
      case NotificationType.CONTACTS_IMPORTED:
        return (
          <ContactsImportedNotificationItem
            notification={notification.data as ContactsImportedNotification}
          />
        );
      case NotificationType.IMPORTING_PROCESS:
      case NotificationType.IMPORTING_PROCESS_FINISHED:
        return (
          <ImportingProcessNotificationItem
            notification={notification.data as ImportingProcessNotification}
          />
        );
      case NotificationType.CONTACT_EXPORTED:
        return (
          <ContactExportedNotificationItem
            notification={notification.data as ContactExportedNotification}
          />
        );
      case NotificationType.NEW_CONTACT:
        return (
          <NewContactNotificationItem
            notification={notification.data as NewContactsNotification}
            variant={variant}
            isDarkMode={isDarkMode}
          />
        );
      case NotificationType.TASK_ASSIGNMENT:
        return (
          <TaskAssignmentNotificationItem
            notification={notification.data as TaskAssignmentNotification}
            variant={variant}
            isDarkMode={isDarkMode}
          />
        );
      default:
        return null;
    }
  }, [notification, variant, isDarkMode]);

  const markAsRead = async () => {
    if (!company?.id) return;
    await putReadNotification(company.id, notification.id);
    await getUnreadNotifications(company.id);
    await getNotifications(company.id, 1);
  };

  const handleDelete = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      if (!company?.id) return;
      await deleteNotification(company.id, notification.id);
    },
    [company?.id, notification.id, deleteNotification]
  );

  const absolute = formatNotificationAbsoluteTime(notification);
  const relative = formatNotificationRelativeTime(notification);

  return (
    <div
      className={cn("notification-container", {
        "notification-container--unread": unread,
        "notification-container--dark": isDarkMode,
        "notification-container--page": variant === "page",
        "notification-container--dropdown": variant === "dropdown",
      })}
    >
      {variant === "dropdown" ? (
        <>
          <DropdownItem
            className={cn("notification-container__dropdown-item flex-grow-1", {
              "fw-bold": unread,
            })}
            onClick={() => handleSelectNotification()}
          >
            {getNotificationItem()}
          </DropdownItem>
          <div className="notification-container__dropdown-actions">
            {unread ? (
              <button
                type="button"
                className="notification-container__mark-read"
                title={intl.formatMessage({
                  id: "app.notifications.markOneRead",
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  markAsRead();
                }}
              >
                <FontAwesomeIcon icon={["fas", "check"]} />
              </button>
            ) : null}
            <button
              type="button"
              className="notification-container__delete"
              title={intl.formatMessage({
                id: "app.navbar.deleteNotification",
              })}
              onClick={handleDelete}
            >
              <FontAwesomeIcon icon={["fas", "trash-alt"]} />
            </button>
          </div>
        </>
      ) : (
        <>
          <div
            className="notification-container__page-body"
            role="button"
            tabIndex={0}
            onClick={() => handleSelectNotification()}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleSelectNotification();
              }
            }}
          >
            <div className="notification-container__strip">
              <span className="notification-container__strip-time">
                <FontAwesomeIcon icon={["far", "clock"]} className="mr-2" />
                <span>{absolute}</span>
                {relative && relative !== "—" ? (
                  <>
                    <span className="notification-container__strip-sep">·</span>
                    <span>{relative}</span>
                  </>
                ) : null}
              </span>
              <span className="notification-container__strip-kind">
                {kindLabel}
              </span>
            </div>
            <p className="notification-container__hint">
              {intl.formatMessage({ id: "app.notifications.pageClickHint" })}
            </p>
            <div className="notification-container__page-inner">
              {getNotificationItem()}
            </div>
          </div>
          <div className="notification-container__page-actions">
            {unread ? (
              <button
                type="button"
                className="notification-container__mark-read"
                title={intl.formatMessage({
                  id: "app.notifications.markOneRead",
                })}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  markAsRead();
                }}
              >
                <FontAwesomeIcon icon={["fas", "check"]} />
              </button>
            ) : null}
            <button
              type="button"
              className="notification-container__delete"
              title={intl.formatMessage({
                id: "app.navbar.deleteNotification",
              })}
              onClick={handleDelete}
            >
              <FontAwesomeIcon icon={["fas", "trash-alt"]} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
